/**
 * Dashboard Customize Panel - Konfiguration der Kacheln
 */

import { useState } from 'react';
import {
  Settings2, X, Eye, EyeOff, RotateCcw,
  Star, Clock, Sun, Calendar, CheckSquare,
  Inbox, Euro, Scale, Network, FileText, QrCode
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from '@/components/ui/sheet';

const TILE_ICONS = {
  favoriten: Star,
  zuletzt_angesehen: Clock,
  briefing: Sun,
  qr_ueberweisung: QrCode,
  fristen: Calendar,
  aufgaben: CheckSquare,
  posteingang: Inbox,
  verguetung: Euro,
  schulden: Scale,
  netzwerk: Network,
  wochenbericht: FileText,
};

const TILE_LABELS = {
  favoriten: 'Favoriten-Klienten',
  zuletzt_angesehen: 'Zuletzt angesehen',
  briefing: 'Morgen-Briefing',
  qr_ueberweisung: 'QR-Code Generierung',
  fristen: 'Fristen-Übersicht',
  aufgaben: 'Offene Aufgaben',
  posteingang: 'Posteingang',
  verguetung: 'Vergütung',
  schulden: 'Schulden-Übersicht (veraltet)',
  netzwerk: 'Netzwerk-Schnellzugriff',
  wochenbericht: 'Wochenbericht',
};

export default function DashboardCustomizePanel({ 
  tiles, 
  onTileVisibilityChange,
  onReset,
  isEditing,
  onEditingChange
}) {
  const [open, setOpen] = useState(false);

  const visibleCount = tiles.filter(t => t.visible).length;
  const totalCount = tiles.length;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button 
          variant={isEditing ? "default" : "outline"} 
          size="sm"
          data-testid="customize-dashboard-btn"
        >
          <Settings2 className="w-4 h-4 mr-2" />
          {isEditing ? 'Bearbeitung beenden' : 'Startseite anpassen'}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Settings2 className="w-5 h-5" />
            Startseite anpassen
          </SheetTitle>
          <SheetDescription>
            Wählen Sie die Kacheln aus, die auf Ihrer Startseite angezeigt werden sollen.
            {isEditing && ' Ziehen Sie Kacheln um sie neu anzuordnen.'}
          </SheetDescription>
        </SheetHeader>

        <div className="py-6 space-y-6">
          {/* Edit Mode Toggle */}
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-100">
            <div>
              <p className="font-medium text-blue-800">Bearbeitungsmodus</p>
              <p className="text-sm text-blue-600">Kacheln per Drag & Drop verschieben</p>
            </div>
            <Switch
              checked={isEditing}
              onCheckedChange={onEditingChange}
              data-testid="edit-mode-toggle"
            />
          </div>

          {/* Tile List */}
          <div className="space-y-2">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-slate-700">
                Kacheln ({visibleCount}/{totalCount} sichtbar)
              </h4>
            </div>
            
            {tiles.map((tile) => {
              const Icon = TILE_ICONS[tile.id] || FileText;
              const label = TILE_LABELS[tile.id] || tile.id;
              const isFixed = tile.id === 'favoriten'; // Nur Favoriten fixiert
              
              return (
                <div
                  key={tile.id}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                    tile.visible 
                      ? 'bg-white border-slate-200' 
                      : 'bg-slate-50 border-slate-100'
                  }`}
                  data-testid={`tile-config-${tile.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      tile.visible ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'
                    }`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${tile.visible ? 'text-slate-800' : 'text-slate-500'}`}>
                        {label}
                      </p>
                      <p className="text-xs text-slate-400">
                        {tile.size === 'compact' ? 'Kompakt' : tile.size === 'full' ? 'Volle Breite' : 'Groß'}
                        {isFixed && ' · Immer sichtbar'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {!isFixed ? (
                      <button
                        onClick={() => onTileVisibilityChange(tile.id, !tile.visible)}
                        className={`p-2 rounded-lg transition-colors ${
                          tile.visible 
                            ? 'text-blue-600 hover:bg-blue-50' 
                            : 'text-slate-400 hover:bg-slate-100'
                        }`}
                        title={tile.visible ? 'Ausblenden' : 'Einblenden'}
                        data-testid={`tile-toggle-${tile.id}`}
                      >
                        {tile.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    ) : (
                      <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                        Fixiert
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <SheetFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={onReset}
            className="flex-1"
            data-testid="reset-layout-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Standard wiederherstellen
          </Button>
          <Button
            onClick={() => { setOpen(false); onEditingChange(false); }}
            className="flex-1"
          >
            Fertig
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
